import { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/useAuthStore';
import ProtectedRoute from '@/components/ProtectedRoute';
import LoginPage from '@/pages/LoginPage';
import RegisterPage from '@/pages/RegisterPage';
import DashboardPage from '@/pages/DashboardPage';
import VideosPage from '@/pages/VideosPage';
import ChannelsPage from '@/pages/ChannelsPage';
import SchedulesPage from '@/pages/SchedulesPage';
import ScheduleTemplatesPage from '@/pages/ScheduleTemplatesPage';
import ImportPage from '@/pages/ImportPage';

function App() {
  const { isAuthenticated, loadUser } = useAuthStore();
  const [isInitializing, setIsInitializing] = useState(true);

  // Initialize app: validate token and load user on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // If we have a token, try to load the user
        if (isAuthenticated) {
          await loadUser();
        }
      } catch (error) {
        console.error('Failed to initialize auth:', error);
      } finally {
        setIsInitializing(false);
      }
    };

    initializeAuth();
  }, []); // Run only once on mount

  // Show loading spinner while initializing
  if (isInitializing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={
            isAuthenticated ? <Navigate to="/dashboard" replace /> : <LoginPage />
          }
        />
        <Route
          path="/register"
          element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RegisterPage />
            )
          }
        />

        {/* Protected Routes */}
        <Route element={<ProtectedRoute />}>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/videos" element={<VideosPage />} />
          <Route path="/channels" element={<ChannelsPage />} />
          <Route path="/schedule-templates" element={<ScheduleTemplatesPage />} />
          <Route path="/schedules" element={<SchedulesPage />} />
          <Route path="/import" element={<ImportPage />} />
        </Route>

        {/* Default Route */}
        <Route
          path="/"
          element={
            <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />
          }
        />

        {/* Catch-all route */}
        <Route
          path="*"
          element={
            <Navigate to={isAuthenticated ? '/dashboard' : '/login'} replace />
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
